import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import Download from "@insite/mobius/Icons/Download";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsDownloadsPanelStyles {
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    buttonListWrapper?: InjectableCss;
    downloadButton?: ButtonPresentationProps;
    downloadIcon?: IconProps;
    noDocumentsText?: TypographyPresentationProps;
}

export const downloadsPanelStyles: ProductDetailsDownloadsPanelStyles = {
    wrapper: {
        css: css`
        `,
    },
    titleWrapper: {
        css: css`
            margin-bottom: 16px;
        `,
    },
    titleText: {
        variant: "h4",
        weight: "bold",
        css: css`
            font-size: 18px;
            color: ${({ theme }) => theme.colors.secondary.contrast};
            margin: 0;
        `,
    },
    buttonListWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 16px;
        `,
    },
    downloadButton: {
        css: css`
            display: flex;
            align-items: center;
            gap: 4px;

            &:hover {
            
                span {
                    color: ${({ theme }) => theme.colors.primary.contrast};
                }
            }

            span {
            
                &[class^="TypographyStyle-"] {
                    font-size: 14px;
                    font-weight: 600;
                    flex: 1;
                }
            }
        `,
    },
    downloadIcon: {
        src: Download,
        css: css`
            color: ${({ theme }) => theme.colors.primary.main};
            width: 15px;
            height: 15px;
        `,
    },
    noDocumentsText: {
        css: css`
            color:  ${({ theme }) => theme.colors.common.disabled};
            font-size: 14px;
            font-style: italic;
        `,
    },
};

const styles = downloadsPanelStyles;

const ProductDetailsDownloadsPanel: React.FC<Props> = ({ product }) => {
    const { documents } = product;

    if (!documents || documents.length === 0) {return null;}

    const handleDownloadClick = (filePath: string) => {
        window.open(filePath, "_blank", "noopener,noreferrer");
    };

    return (
        <StyledWrapper
            {...styles.wrapper}
            data-test-selector="productDetails_downloadsPanel"
        >
            
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.titleText}>{translate("Downloads")}</Typography>
            </StyledWrapper>

            <StyledWrapper {...styles.buttonListWrapper}>
                {documents.map(document => (
                    <Button
                        key={document.id.toString()}
                        {...styles.downloadButton}
                        onClick={() => handleDownloadClick(document.filePath)}
                        data-test-selector={`download_${document.id}`}
                    >
                        <Typography>{document.name}</Typography>
                        <IconMemo {...styles.downloadIcon} />
                    </Button>
                ))}
            </StyledWrapper>

        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsDownloadsPanel),
    definition: {
        displayName: "Downloads Panel",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
